'use client'

import { useMemo } from 'react'
import { useDashboardData } from '@/hooks/useDashboardData'
import { useFilteredData } from '@/hooks/useFilters'
import { SLA_THRESHOLDS } from '@/lib/calculations'
import Badge from '@/components/ui/Badge'
import type { Task } from '@/types'

interface ClientHealth {
  name: string
  cat: string
  health: 'green' | 'yellow' | 'red'
  score: number
  active: number
  done30: number
  slaAdh: number | null
  hrs30: number
  estAcc: number | null
  lastDone: string | null
  projCount: number
}

export default function ClientsPage() {
  const { isLoading, activeProjectIds } = useDashboardData()
  const { filteredTasks, filteredTS } = useFilteredData()

  const clients = useMemo(() => {
    const now = new Date()
    const thirtyStr = new Date(now.getTime() - 30 * 864e5).toISOString().slice(0, 10)
    const sixtyStr = new Date(now.getTime() - 60 * 864e5).toISOString().slice(0, 10)

    // Pre-build timesheet hours by project name
    const tsByProj: Record<string, number> = {}
    filteredTS.filter(ts => ts.date >= thirtyStr).forEach(ts => {
      tsByProj[ts.proj] = (tsByProj[ts.proj] || 0) + ts.hours
    })

    // Group tasks by client
    const groups: Record<string, { name: string; tasks: Task[]; cat: string; projNames: Set<string> }> = {}
    filteredTasks.forEach(t => {
      if (!activeProjectIds.includes(t.projId)) return
      const client = t.client || t.proj
      if (!groups[client]) groups[client] = { name: client, tasks: [], cat: t.cat, projNames: new Set() }
      groups[client].tasks.push(t)
      groups[client].projNames.add(t.proj)
    })

    const result: ClientHealth[] = Object.values(groups).map(c => {
      const active = c.tasks.filter(t => t.stage !== 'Done' && t.stage !== 'Backlog' && !t.cancelled)
      const done30 = c.tasks.filter(t => t.stage === 'Done' && t.last_stage >= thirtyStr)
      const done60 = c.tasks.filter(t => t.stage === 'Done' && t.last_stage >= sixtyStr && t.last_stage < thirtyStr)

      let slaOk = 0
      done30.forEach(t => { if (t.lead !== null && t.lead <= (SLA_THRESHOLDS[t.cat] || 30)) slaOk++ })
      const slaAdh = done30.length > 0 ? (slaOk / done30.length) * 100 : null

      let hrs30 = 0
      c.projNames.forEach(pn => { hrs30 += tsByProj[pn] || 0 })

      const estTasks = done30.filter(t => t.alloc_h > 0 && t.eff_h > 0)
      const estAcc = estTasks.length > 0
        ? estTasks.map(t => Math.max(0, 100 - Math.abs(100 - (t.eff_h / t.alloc_h) * 100))).reduce((a, b) => a + b, 0) / estTasks.length
        : null

      const lastDone = done30.length > 0 ? done30.sort((a, b) => b.last_stage.localeCompare(a.last_stage))[0].last_stage : null

      let score = 50
      if (slaAdh !== null) score += (slaAdh - 80) * 0.5
      if (active.length > 20) score -= 10
      if (done30.length > done60.length) score += 5
      if (estAcc !== null && estAcc > 80) score += 10
      score = Math.max(0, Math.min(100, score))

      const health: 'green' | 'yellow' | 'red' = score >= 70 ? 'green' : score >= 45 ? 'yellow' : 'red'

      return { name: c.name, cat: c.cat, health, score, active: active.length, done30: done30.length, slaAdh, hrs30, estAcc, lastDone, projCount: c.projNames.size }
    }).filter(c => c.active > 0 || c.done30 > 0).sort((a, b) => a.score - b.score)

    return result
  }, [filteredTasks, filteredTS, activeProjectIds])

  if (isLoading) return <div className="text-slate-400 text-center py-16">Loading...</div>

  const healthColors = { green: 'bg-emerald-500', yellow: 'bg-amber-500', red: 'bg-red-500' }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Client Health <span className="text-sm font-normal text-slate-400 ml-1">{clients.length} clients</span></h3>
          <p className="text-sm text-slate-400">Sorted worst-first. Green = on track, Yellow = needs attention, Red = at risk.</p>
        </div>
        <div className="flex gap-3 text-sm text-slate-400">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> On track</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Attention</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> At risk</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {clients.map(c => (
          <div key={c.name} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 hover:border-blue-300 dark:hover:border-blue-800 transition-colors">
            <div className="flex items-start justify-between mb-3">
              <div className="min-w-0">
                <div className="font-semibold text-base font-[family-name:var(--font-display)] truncate">{c.name}</div>
                <div className="text-sm uppercase tracking-wider text-slate-400">{c.cat} · {c.projCount} project{c.projCount > 1 ? 's' : ''}</div>
              </div>
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 mt-1 ${healthColors[c.health]}`} />
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Active tasks</span>
                <span className="font-semibold">{c.active}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Completed (30d)</span>
                <span className="font-semibold">{c.done30}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">SLA Adherence</span>
                {c.slaAdh !== null
                  ? <Badge variant={c.slaAdh >= 90 ? 'success' : c.slaAdh >= 70 ? 'warning' : 'danger'}>{c.slaAdh.toFixed(0)}%</Badge>
                  : <span className="text-slate-400">—</span>}
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Hours (30d)</span>
                <span className="font-semibold">{c.hrs30.toFixed(0)}h</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Est. Accuracy</span>
                {c.estAcc !== null
                  ? <Badge variant={c.estAcc >= 80 ? 'success' : c.estAcc >= 60 ? 'warning' : 'danger'}>{c.estAcc.toFixed(0)}%</Badge>
                  : <span className="text-slate-400">—</span>}
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Last completed</span>
                <span className="text-slate-500 text-sm">{c.lastDone || 'none'}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
